import classNames from 'classnames';
import React, { forwardRef, useCallback, memo } from 'react';
import { FormFields } from '../../common/types';
import styles from './Input.module.scss';

export type OnChangeEvent = React.ChangeEvent<HTMLInputElement>;

export interface InputProps {
    name?: keyof FormFields | '' | null;
    label?: string | null;
    placeholder?: string | null;
    value?: string;
    onChange?: (e: OnChangeEvent) => void;
    onClear?: () => void;
    error?: string | null;
    className?: string;
    autoComplete?: 'on' | 'off';
    resetField?: (name: keyof FormFields, options: { defaultValue: string }) => void;
}

const defaultProps: InputProps = {
    name: '',
    label: null,
    placeholder: '',
    value: '',
    onChange: () => null,
    onClear: () => null,
    error: null,
    className: '',
    autoComplete: 'off',
    resetField: () => {},
};

const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            name,
            label,
            placeholder,
            value,
            onChange,
            error,
            onClear,
            className,
            autoComplete,
            resetField,
            ...props
        },
        ref
    ): JSX.Element => {
        const id = React.useId();
        const clean = useCallback(() => {
            if (name) resetField?.(name, { defaultValue: '' });
            onChange?.({ target: { value: '' } } as OnChangeEvent);
        }, [name, resetField, onChange]);
        return (
            <div
                className={classNames(
                    styles['input-group'],
                    { [styles['group-error']]: error },
                    className
                )}
            >
                {label && (
                    <label className={styles['input-label']} htmlFor={id}>
                        {label}
                    </label>
                )}
                <div className={styles['field-group']}>
                    {/* eslint-disable react/jsx-props-no-spreading */}
                    <input
                        {...props}
                        name={name ?? ''}
                        value={value}
                        onChange={onChange}
                        type="text"
                        placeholder={placeholder ?? ''}
                        id={id}
                        ref={ref}
                        autoComplete={autoComplete}
                    />
                    {/* eslint-enable react/jsx-props-no-spreading */}
                    <button
                        className={styles['clean-icon']}
                        type="button"
                        aria-label="очистить"
                        onClick={clean}
                    />
                    {error && <span className={styles['error-text']}>{error}</span>}
                </div>
            </div>
        );
    }
);

Input.defaultProps = defaultProps;

export default memo(Input);
